import { useState, useEffect, useRef, useCallback } from "react";

const BASE = "https://support.gs-group.fr/api";
const hdr = () => ({ Authorization:"Bearer "+localStorage.getItem("nexo_token") });

const norm = s => (s||"").toLowerCase()
  .normalize("NFD").replace(/[̀-ͯ]/g,"")
  .replace(/[-_/]/g," ").trim();

/* ── Navigation modules ── */
const NAV_COMMANDS = [
  { id:"dashboard",   lbl:"Dashboard",          ic:"🏠", sub:"Vue générale & synthèse",       aliases:["accueil","home","tableau de bord","synthese"] },
  { id:"pipeline",    lbl:"Pipeline",            ic:"📋", sub:"Kanban SAV & interventions",    aliases:["pipeline","kanban","board","flux"] },
  { id:"taches",      lbl:"Tâches",              ic:"✅", sub:"Mes tâches personnelles",       aliases:["taches","tache","todo","task"] },
  { id:"planning",    lbl:"Planning",            ic:"📅", sub:"Calendrier d'interventions",    aliases:["planning","calendrier","agenda","rdv"] },
  { id:"carte",       lbl:"Carte",               ic:"🗺️", sub:"Géolocalisation",               aliases:["carte","map","geo","localisation"] },
  { id:"parc-it",     lbl:"Parc IT",             ic:"🖥️", sub:"Équipements & réseau",          aliases:["parc it","informatique","pc","ordinateur","camera","ip","reseau"] },
  { id:"clients",     lbl:"Clients",             ic:"👥", sub:"Base clients & contacts",       aliases:["client","clients","societe","entreprise","contact"] },
  { id:"stock",       lbl:"Stock",               ic:"📦", sub:"Inventaire & produits",         aliases:["stock","inventaire","produit","materiel","reference"] },
  { id:"vehicules",   lbl:"Flotte / Véhicules",  ic:"🚗", sub:"Parc automobile",               aliases:["voiture","vehicule","flotte","plaque","immat","camion"] },
  { id:"stats",       lbl:"Statistiques",        ic:"📊", sub:"Rapports & analyses",           aliases:["stats","statistiques","rapport","kpi","chiffre"] },
  { id:"heures",      lbl:"Heures",              ic:"⏱️", sub:"Suivi du temps & pointage",     aliases:["heures","heure","temps","pointage","presence"] },
  { id:"settings",    lbl:"Paramètres",          ic:"⚙️", sub:"Configuration du CRM",          aliases:["parametres","reglages","settings","config","compte"] },
];

/* ── Commandes d'action ── */
const ACTION_COMMANDS = [
  { id:"action:new-ticket",  lbl:"Créer un ticket SAV",      ic:"🎫", sub:"Ouvrir le formulaire SAV",           aliases:["creer ticket","nouveau ticket","signaler","panne","support","sav"] },
  { id:"action:new-int",     lbl:"Créer une intervention",   ic:"🔧", sub:"Planifier une intervention",         aliases:["creer inter","nouvelle inter","intervention","planifier","rdv"] },
  { id:"action:new-task",    lbl:"Nouvelle tâche",           ic:"✅", sub:"Ajouter une tâche personnelle",      aliases:["creer tache","nouvelle tache","todo","ajouter tache","rappel"] },
  { id:"action:new-client",  lbl:"Ajouter un client",        ic:"👥", sub:"Enregistrer un nouveau client",      aliases:["creer client","ajouter client","nouveau client"] },
];

/* ── Couleurs par catégorie ── */
const TYPE_COLOR = {
  cmd:"#6366f1", action:"#f59e0b",
  vehicule:"#3b82f6", client:"#10b981",
  intervention:"#8b5cf6", ticket:"#ef4444",
  tache:"#06b6d4", "parc-it":"#6366f1", stock:"#f97316",
};

const CATS = {
  action:"Actions rapides ⚡",
  cmd:"Navigation 🗂️",
  intervention:"Interventions 🔧",
  ticket:"Tickets SAV 🎫",
  client:"Clients 👥",
  tache:"Tâches ✅",
  vehicule:"Véhicules 🚗",
  "parc-it":"Parc IT 🖥️",
  stock:"Stock 📦",
};

/* ── Ordre d'affichage des groupes ── */
const GROUP_ORDER = ["action","cmd","intervention","ticket","client","tache","vehicule","parc-it","stock"];

export default function GlobalSearch({ T, onClose, onNavigate, vehicules=[], clients=[], interventions=[], tickets=[] }) {
  const [q, setQ]           = useState("");
  const [results, setResults] = useState([]);
  const [idx, setIdx]       = useState(0);
  const [loading, setLoading] = useState(false);
  const [todos, setTodos]     = useState([]);
  const [parcItems, setParcItems] = useState([]);
  const [stock, setStock]     = useState([]);
  const inputRef            = useRef(null);
  const containerRef        = useRef(null);
  const listRef             = useRef(null);

  /* ── Chargement données au montage ── */
  useEffect(()=>{
    fetch(`${BASE}/todos`,{headers:hdr()}).then(r=>r.json()).then(d=>setTodos(Array.isArray(d)?d:[])).catch(()=>{});
    fetch(`${BASE}/stock`,{headers:hdr()}).then(r=>r.json()).then(d=>setStock(Array.isArray(d)?d:[])).catch(()=>{});
    Promise.all([
      fetch(`${BASE}/parc-it`,{headers:hdr()}).then(r=>r.json()).catch(()=>[]),
      fetch(`${BASE}/parc-it/cameras`,{headers:hdr()}).then(r=>r.json()).catch(()=>[]),
    ]).then(([items,cams])=>{
      setParcItems([
        ...(Array.isArray(items)?items:[]).map(x=>({...x,_cat:"équipement"})),
        ...(Array.isArray(cams)?cams:[]).map(x=>({...x,_cat:"caméra",nom:x.nom_label||x.cam_id})),
      ]);
    });
  },[]);

  /* ── Focus + fermeture ── */
  useEffect(()=>{
    inputRef.current?.focus();
    const fn = e => {
      if(e.key==="Escape") onClose();
      if(containerRef.current && !containerRef.current.contains(e.target) && e.type==="mousedown") onClose();
    };
    document.addEventListener("keydown", fn);
    document.addEventListener("mousedown", fn);
    return ()=>{ document.removeEventListener("keydown",fn); document.removeEventListener("mousedown",fn); };
  },[onClose]);

  /* ── Recalcul résultats ── */
  useEffect(()=>{
    const t = setTimeout(()=>compute(q), q ? 100 : 0);
    return ()=>clearTimeout(t);
  },[q, vehicules, clients, interventions, tickets, todos, parcItems, stock]);

  /* ── Scroll item actif ── */
  useEffect(()=>{
    listRef.current?.querySelector(`[data-idx="${idx}"]`)?.scrollIntoView({block:"nearest"});
  },[idx]);

  const matchScore = (query, fields) => {
    let best = 0;
    for (const f of fields) {
      if (!f) continue;
      const n = norm(String(f));
      if (n === query)               best = Math.max(best, 100);
      else if (n.startsWith(query))  best = Math.max(best, 80);
      else if (n.includes(query))    best = Math.max(best, 60);
      else {
        const words = query.split(" ").filter(Boolean);
        if (words.length > 1 && words.every(w => n.includes(w))) best = Math.max(best, 40);
      }
    }
    return best;
  };

  const compute = useCallback(async (raw) => {
    const n = norm(raw);

    if (!n) {
      setResults([
        ...ACTION_COMMANDS.map(a=>({type:"action",...a})),
        ...NAV_COMMANDS.slice(0,6).map(c=>({type:"cmd",...c})),
      ]);
      setIdx(0);
      return;
    }

    setLoading(true);
    const all = [];

    /* Actions rapides */
    ACTION_COMMANDS.forEach(a=>{
      const score = matchScore(n, [a.lbl, ...a.aliases]);
      if(score>0) all.push({type:"action", score, ...a});
    });

    /* Modules nav */
    NAV_COMMANDS.forEach(c=>{
      const score = matchScore(n, [c.lbl, c.sub, ...c.aliases]);
      if(score>0) all.push({type:"cmd", score, ...c});
    });

    /* Véhicules */
    vehicules.forEach(v=>{
      const fields = [v.immatriculation, v.marque, v.modele, v.technicien_nom];
      try {
        const extra = JSON.parse(localStorage.getItem("gs_veh_extra")||"{}");
        const e = extra[v.immatriculation]||{};
        if(e.titulaire) fields.push(e.titulaire);
        if(e.groupe)    fields.push(e.groupe);
      } catch{}
      const score = matchScore(n, fields);
      if(score>0) all.push({type:"vehicule", score, item:v,
        lbl:`${v.marque||""} ${v.modele||""}`.trim()||v.immatriculation,
        sub:v.immatriculation, ic:"🚗"});
    });

    /* Clients */
    clients.forEach(c=>{
      const score = matchScore(n, [c.nom, c.raison_sociale, c.email, c.telephone, c.ville, c.adresse]);
      if(score>0) all.push({type:"client", score, item:c,
        lbl:c.nom||c.raison_sociale,
        sub:[c.ville, c.email].filter(Boolean).join(" · "), ic:"👥"});
    });

    /* Interventions */
    interventions.slice(0,300).forEach(i=>{
      const score = matchScore(n, [i.titre, i.client_nom, i.client_ville, String(i.id)]);
      if(score>0) all.push({type:"intervention", score, item:i,
        lbl:i.titre, sub:[i.client_nom, i.client_ville].filter(Boolean).join(" · "), ic:"🔧"});
    });

    /* Tickets SAV */
    tickets.slice(0,300).forEach(t=>{
      const score = matchScore(n, [t.titre, t.objet, t.sujet, t.client_nom, String(t.id)]);
      if(score>0) all.push({type:"ticket", score, item:t,
        lbl:t.titre||t.objet||t.sujet||`Ticket #${t.id}`,
        sub:[t.client_nom, t.statut].filter(Boolean).join(" · "), ic:"🎫"});
    });

    /* Tâches */
    todos.forEach(t=>{
      const tags = Array.isArray(t.tags)?t.tags.join(" "):"";
      const score = matchScore(n, [t.titre, t.description, tags]);
      if(score>0) all.push({type:"tache", score, item:t,
        lbl:t.titre,
        sub:t.statut==="done"?"Terminé":t.statut==="in_progress"?"En cours":"À faire",
        ic:"✅"});
    });

    /* Parc IT */
    parcItems.forEach(p=>{
      const score = matchScore(n, [
        p.nom, p.hostname, p.adresse_ip, p.ip_connectee,
        p.type_equipement, p.marque, p.modele, p.numero_serie,
        p.utilisateur, p.service, p.localisation, p.vlan, p.inv_id,
        p.cam_id, p.nom_label, p.nvr, p.type_camera,
      ]);
      if(score>0) all.push({type:"parc-it", score, item:p,
        lbl:p.nom||p.hostname||p.nom_label||p.cam_id||p.type_equipement||"Équipement",
        sub:[p.adresse_ip||p.ip_connectee, p.utilisateur||p.localisation, p._cat].filter(Boolean).join(" · "),
        ic:p._cat==="caméra"?"📷":"🖥️"});
    });

    /* Stock */
    stock.forEach(p=>{
      const score = matchScore(n, [p.designation, p.reference, p.categorie, p.marque, p.numero_serie, p.notes]);
      if(score>0) all.push({type:"stock", score, item:p,
        lbl:p.designation,
        sub:[p.reference, p.categorie, p.marque].filter(Boolean).join(" · "),
        ic:"📦"});
    });

    /* Fallback API si résultats maigres */
    if(all.filter(r=>r.type!=="cmd"&&r.type!=="action").length<3 && n.length>=3){
      try {
        const [apiInts, apiSav, apiClients] = await Promise.all([
          fetch(`${BASE}/interventions?search=${encodeURIComponent(raw)}`,{headers:hdr()}).then(r=>r.json()).catch(()=>[]),
          fetch(`${BASE}/tickets?search=${encodeURIComponent(raw)}`,{headers:hdr()}).then(r=>r.json()).catch(()=>[]),
          fetch(`${BASE}/clients?search=${encodeURIComponent(raw)}`,{headers:hdr()}).then(r=>r.json()).catch(()=>[]),
        ]);
        const exInt = new Set(all.filter(r=>r.type==="intervention").map(r=>r.item?.id));
        const exSav = new Set(all.filter(r=>r.type==="ticket").map(r=>r.item?.id));
        const exCli = new Set(all.filter(r=>r.type==="client").map(r=>r.item?.id));
        (Array.isArray(apiInts)?apiInts:[]).slice(0,4).forEach(i=>{
          if(!exInt.has(i.id)) all.push({type:"intervention",score:50,item:i,lbl:i.titre,sub:[i.client_nom,i.client_ville].filter(Boolean).join(" · "),ic:"🔧"});
        });
        (Array.isArray(apiSav)?apiSav:[]).slice(0,4).forEach(t=>{
          if(!exSav.has(t.id)) all.push({type:"ticket",score:50,item:t,lbl:t.titre||t.objet||`Ticket #${t.id}`,sub:[t.client_nom,t.statut].filter(Boolean).join(" · "),ic:"🎫"});
        });
        (Array.isArray(apiClients)?apiClients:[]).slice(0,3).forEach(c=>{
          if(!exCli.has(c.id)) all.push({type:"client",score:50,item:c,lbl:c.nom||c.raison_sociale,sub:[c.ville,c.email].filter(Boolean).join(" · "),ic:"👥"});
        });
      } catch{}
    }

    /* Tri : actions > data (score desc) > nav */
    all.sort((a,b)=>{
      const orderA = a.type==="action"?0:a.type==="cmd"?2:1;
      const orderB = b.type==="action"?0:b.type==="cmd"?2:1;
      if(orderA!==orderB) return orderA-orderB;
      return b.score - a.score;
    });

    setResults(all.slice(0,18));
    setIdx(0);
    setLoading(false);
  },[vehicules, clients, interventions, tickets, todos, parcItems, stock]);

  /* ── Clavier ── */
  const onKey = e => {
    if(e.key==="ArrowDown"){ e.preventDefault(); setIdx(i=>Math.min(i+1,results.length-1)); }
    else if(e.key==="ArrowUp"){ e.preventDefault(); setIdx(i=>Math.max(i-1,0)); }
    else if(e.key==="Enter"){ e.preventDefault(); if(results[idx]) activate(results[idx]); }
  };

  /* ── Activation ── */
  const activate = r => {
    if(r.type==="action")       onNavigate("action", r.id);
    else if(r.type==="cmd")          onNavigate(r.id, null);
    else if(r.type==="vehicule")     onNavigate("vehicules", r.item);
    else if(r.type==="client")       onNavigate("clients", r.item);
    else if(r.type==="intervention") onNavigate("interventions", r.item);
    else if(r.type==="ticket")       onNavigate("sav", r.item);
    else if(r.type==="tache")        onNavigate("taches", r.item);
    else if(r.type==="parc-it")      onNavigate("parc-it", r.item);
    else if(r.type==="stock")        onNavigate("stock", r.item);
    onClose();
  };

  /* ── Groupement ordonné ── */
  const grouped = {};
  results.forEach((r,i)=>{ const k=r.type; if(!grouped[k]) grouped[k]=[]; grouped[k].push({...r,_idx:i}); });
  const groupEntries = GROUP_ORDER.filter(k=>grouped[k]).map(k=>[k,grouped[k]]);

  const hasData = results.some(r=>r.type!=="cmd"&&r.type!=="action");
  const empty   = q.length>1 && !loading && results.length===0;
  const showDefault = !q;

  return (
    <div style={{
      position:"fixed",inset:0,
      background:"rgba(0,0,0,0.6)",
      backdropFilter:"blur(6px)",
      WebkitBackdropFilter:"blur(6px)",
      zIndex:600,
      display:"flex",alignItems:"flex-start",justifyContent:"center",
      padding:"clamp(60px,10vh,120px) 20px 20px",
      animation:"gs-fade 0.15s ease",
    }}>
      <style>{`
        @keyframes gs-fade{from{opacity:0}to{opacity:1}}
        @keyframes gs-up{from{opacity:0;transform:translateY(-12px) scale(0.97)}to{opacity:1;transform:translateY(0) scale(1)}}
        @keyframes gs-spin{to{transform:rotate(360deg)}}
        .gs-row{transition:background 0.07s;}
        .gs-row:hover{background:var(--gs-hover)!important}
      `}</style>

      <div ref={containerRef} style={{
        background:T.card,
        border:`1px solid ${T.border}`,
        borderRadius:20,
        width:"100%",maxWidth:640,
        boxShadow:"0 32px 80px rgba(0,0,0,0.4), 0 8px 24px rgba(0,0,0,0.2)",
        overflow:"hidden",
        animation:"gs-up 0.2s cubic-bezier(0.16,1,0.3,1)",
        '--gs-hover':T.accentDim,
      }}>

        {/* ── Input ── */}
        <div style={{display:"flex",alignItems:"center",gap:14,padding:"16px 20px",borderBottom:`1px solid ${T.border}`}}>
          {loading
            ? <div style={{width:18,height:18,border:`2.5px solid ${T.accent}`,borderTopColor:"transparent",borderRadius:"50%",animation:"gs-spin 0.7s linear infinite",flexShrink:0}}/>
            : <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={T.muted} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{flexShrink:0}}>
                <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
              </svg>
          }
          <input
            ref={inputRef}
            value={q}
            onChange={e=>{setQ(e.target.value);setIdx(0);}}
            onKeyDown={onKey}
            placeholder="Rechercher ou lancer une action…"
            style={{flex:1,background:"none",border:"none",outline:"none",fontSize:17,color:T.text,fontFamily:"inherit",fontWeight:500}}
          />
          {q && <button onClick={()=>{setQ("");inputRef.current?.focus();}}
            style={{background:T.bg,border:`1px solid ${T.border}`,color:T.muted,cursor:"pointer",fontSize:11,padding:"2px 8px",borderRadius:6,lineHeight:1.6,flexShrink:0}}>
            ✕ Effacer
          </button>}
        </div>

        {/* ── Corps ── */}
        <div ref={listRef} style={{maxHeight:460,overflowY:"auto"}}>

          {/* Vide */}
          {empty&&(
            <div style={{padding:"40px 20px",textAlign:"center"}}>
              <div style={{fontSize:40,marginBottom:12,opacity:0.4}}>🔍</div>
              <div style={{fontSize:14,fontWeight:700,color:T.text,marginBottom:6}}>Aucun résultat pour « {q} »</div>
              <div style={{fontSize:12,color:T.muted}}>Essayez une plaque, un nom, une IP, une référence…</div>
            </div>
          )}

          {/* Accueil */}
          {showDefault&&(
            <div style={{padding:"16px 18px 12px"}}>
              <div style={{fontSize:10,fontWeight:800,color:T.muted,textTransform:"uppercase",letterSpacing:"1.2px",marginBottom:12}}>Actions rapides</div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:16}}>
                {ACTION_COMMANDS.map(a=>(
                  <button key={a.id} onClick={()=>activate({type:"action",...a})}
                    style={{display:"flex",alignItems:"center",gap:10,padding:"11px 14px",borderRadius:12,border:`1px solid ${T.border}`,background:T.bg,color:T.text,fontSize:13,fontWeight:600,cursor:"pointer",textAlign:"left",transition:"all 0.1s"}}
                    onMouseEnter={e=>{e.currentTarget.style.background=T.accentDim;e.currentTarget.style.borderColor=T.accent;e.currentTarget.style.color=T.accent;}}
                    onMouseLeave={e=>{e.currentTarget.style.background=T.bg;e.currentTarget.style.borderColor=T.border;e.currentTarget.style.color=T.text;}}>
                    <span style={{fontSize:18,lineHeight:1}}>{a.ic}</span>
                    <span>{a.lbl}</span>
                  </button>
                ))}
              </div>
              <div style={{fontSize:10,fontWeight:800,color:T.muted,textTransform:"uppercase",letterSpacing:"1.2px",marginBottom:10}}>Naviguer vers</div>
              <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
                {NAV_COMMANDS.map(c=>(
                  <button key={c.id} onClick={()=>activate({type:"cmd",...c})}
                    style={{display:"flex",alignItems:"center",gap:5,padding:"6px 12px",borderRadius:99,border:`1px solid ${T.border}`,background:T.bg,color:T.muted,fontSize:12,cursor:"pointer",transition:"all 0.1s"}}
                    onMouseEnter={e=>{e.currentTarget.style.background=T.accentDim;e.currentTarget.style.color=T.accent;e.currentTarget.style.borderColor=T.accent;}}
                    onMouseLeave={e=>{e.currentTarget.style.background=T.bg;e.currentTarget.style.color=T.muted;e.currentTarget.style.borderColor=T.border;}}>
                    <span style={{fontSize:13}}>{c.ic}</span>{c.lbl}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Résultats groupés */}
          {!showDefault&&!empty&&groupEntries.map(([type,items])=>{
            const col = TYPE_COLOR[type]||T.accent;
            return (
              <div key={type}>
                <div style={{padding:"10px 20px 3px",display:"flex",alignItems:"center",gap:8}}>
                  <span style={{fontSize:9,fontWeight:800,color:T.muted,textTransform:"uppercase",letterSpacing:"1.2px"}}>{CATS[type]||type}</span>
                  <div style={{flex:1,height:1,background:T.border}}/>
                </div>
                {items.map(r=>{
                  const active = r._idx===idx;
                  return (
                    <div key={r._idx} data-idx={r._idx} className="gs-row"
                      onClick={()=>activate(r)} onMouseEnter={()=>setIdx(r._idx)}
                      style={{display:"flex",alignItems:"center",gap:12,padding:"9px 20px",cursor:"pointer",background:active?T.accentDim:"transparent"}}>

                      {/* Icône */}
                      <div style={{
                        width:34,height:34,borderRadius:10,flexShrink:0,
                        background:active?`${col}22`:`${col}12`,
                        border:`1px solid ${active?col:col+"30"}`,
                        display:"flex",alignItems:"center",justifyContent:"center",fontSize:15,
                      }}>{r.ic}</div>

                      {/* Texte */}
                      <div style={{flex:1,minWidth:0}}>
                        <div style={{fontSize:13,fontWeight:active?700:500,color:active?col:T.text,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                          {highlight(r.lbl||"",q,col,active)}
                        </div>
                        {r.sub&&<div style={{fontSize:11,color:T.muted,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",marginTop:1}}>{r.sub}</div>}
                      </div>

                      {/* Badges droite */}
                      <div style={{display:"flex",alignItems:"center",gap:6,flexShrink:0}}>
                        {type==="vehicule"&&r.item?.immatriculation&&(
                          <span style={{fontSize:10,fontFamily:"monospace",fontWeight:800,color:col,background:`${col}18`,borderRadius:6,padding:"2px 8px"}}>{r.item.immatriculation}</span>
                        )}
                        {(type==="intervention"||type==="ticket")&&r.item?.statut&&(
                          <span style={{fontSize:9,fontWeight:700,padding:"2px 8px",borderRadius:99,background:`${col}15`,color:col}}>{r.item.statut}</span>
                        )}
                        {type==="stock"&&r.item?.quantite!=null&&(
                          <span style={{fontSize:10,fontWeight:700,color:r.item.quantite===0?"#ef4444":col,background:r.item.quantite===0?"rgba(239,68,68,0.1)":`${col}15`,borderRadius:6,padding:"2px 8px"}}>
                            {r.item.quantite===0?"Rupture":`× ${r.item.quantite}`}
                          </span>
                        )}
                        {type==="tache"&&r.item?.priorite&&(
                          <span style={{fontSize:9,fontWeight:700,color:col,background:`${col}15`,borderRadius:6,padding:"2px 7px"}}>{r.item.priorite}</span>
                        )}
                        {type==="action"&&(
                          <span style={{fontSize:9,fontWeight:700,color:col,background:`${col}15`,borderRadius:6,padding:"2px 8px"}}>Action</span>
                        )}
                        {active&&<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={col} strokeWidth="2.5" strokeLinecap="round"><polyline points="9 18 15 12 9 6"/></svg>}
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>

        {/* ── Footer ── */}
        <div style={{padding:"10px 20px",borderTop:`1px solid ${T.border}`,display:"flex",alignItems:"center",gap:14,background:T.bg}}>
          {[["↑↓","naviguer"],["↵","ouvrir"],["Échap","fermer"]].map(([k,l])=>(
            <span key={k} style={{fontSize:10,color:T.muted,display:"flex",alignItems:"center",gap:4}}>
              <kbd style={{background:T.card,border:`1px solid ${T.border}`,borderRadius:5,padding:"1px 6px",fontSize:10,fontFamily:"inherit"}}>{k}</kbd>{l}
            </span>
          ))}
          <span style={{marginLeft:"auto",fontSize:10,color:T.muted,opacity:0.6}}>
            {results.filter(r=>r.type!=="cmd"&&r.type!=="action").length>0
              ? `${results.filter(r=>r.type!=="cmd"&&r.type!=="action").length} résultat${results.filter(r=>r.type!=="cmd"&&r.type!=="action").length>1?"s":""}`
              : "⌘K / Ctrl+K pour ouvrir"}
          </span>
        </div>
      </div>
    </div>
  );
}

/* ── Surlignage ── */
function highlight(text, query, col, active) {
  if(!query||!text) return text;
  const n = norm(text), q = norm(query);
  const i = n.indexOf(q);
  if(i<0) return text;
  return (
    <>
      {text.slice(0,i)}
      <mark style={{background:`${col}35`,color:col,borderRadius:3,padding:"0 2px",fontWeight:800}}>{text.slice(i,i+query.length)}</mark>
      {text.slice(i+query.length)}
    </>
  );
}
